import { App } from "antd";
import PQueue from "p-queue";
import { createContext, type ReactNode, use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { translateServiceError } from "./useServiceError.ts";

type SavingContextValue = {
  readonly saving: boolean;
  readonly error: Error | null;
  readonly trackSaving: (fn: () => Promise<unknown>) => void;
  readonly retry: () => void;
};

const SavingContext = createContext<SavingContextValue>({
  saving: false,
  error: null,
  // Outside a SavingProvider there is no queue to serialize behind — run the
  // task immediately instead of silently dropping it.
  trackSaving: (fn) => void fn(),
  retry: () => {},
});

export const SavingProvider = ({ children }: { readonly children: ReactNode }) => {
  const queueRef = useRef(new PQueue({ concurrency: 1 }));
  const failedFnRef = useRef<(() => Promise<unknown>) | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { t } = useTranslation();
  const { message } = App.useApp();

  useEffect(() => {
    const queue = queueRef.current;
    const onIdle = () => setSaving(false);
    queue.on("idle", onIdle);
    return () => {
      queue.off("idle", onIdle);
    };
  }, []);

  const runTask = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn();
      } catch (e) {
        const serviceMsg = translateServiceError(e, t);
        if (serviceMsg != null) {
          message.error(serviceMsg);
          return;
        }
        failedFnRef.current = fn;
        setError(e instanceof Error ? e : new Error(String(e)));
        queueRef.current.pause();
      }
    },
    [t, message],
  );

  const trackSaving = useCallback(
    (fn: () => Promise<unknown>) => {
      setSaving(true);
      void queueRef.current.add(() => runTask(fn));
    },
    [runTask],
  );

  const retry = useCallback(() => {
    const fn = failedFnRef.current;
    if (!fn) return;
    failedFnRef.current = null;
    setError(null);
    setSaving(true);
    void queueRef.current.add(() => runTask(fn), { priority: 1 });
    queueRef.current.start();
  }, [runTask]);

  const value = useMemo(() => ({ saving, error, trackSaving, retry }), [saving, error, trackSaving, retry]);

  return <SavingContext value={value}>{children}</SavingContext>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSaving = () => use(SavingContext);
